import { omit } from 'lodash-es'
import { types } from 'mobx-state-tree'

import { ReferenceFreightieOrUser } from '../Actors'
import { FORTO_AVATAR, TAvatarType } from '../avatars/AvatarType'
import { getAvatar, getAvatarType, getTitleFromActor } from '../avatars/avatarUtils'
import { COMPANY_NAME } from '../constants'
import { IActivityMessageSection, IActivityTitle, LABELED, TEXT } from '../messages/MessageSectionType'
import { Activity } from './Activity'

type TBookingRequestActions = 'rejected' | 'approved' | 'requested' | 'expired'

export const BookingRequestActivity = types
  .compose(
    'BookingRequestActivity',
    Activity,
    types.model({
      actor: ReferenceFreightieOrUser,
      action: types.union(
        types.literal('requested'),
        types.literal('approved'),
        types.literal('rejected'),
        types.literal('expired')
      ),
      reason: types.maybe(types.string),
      shipmentId: types.maybe(types.string),
    })
  )
  .preProcessSnapshot((snapshot) => {
    const { freightieAuthor_id, userAuthor_id } = snapshot
    return {
      ...snapshot,
      actor: freightieAuthor_id || userAuthor_id,
      actorType: freightieAuthor_id ? 'freightie' : 'user',
    }
  })
  .actions(() => ({
    postProcessSnapshot(snapshot) {
      const pureActivity = omit(snapshot, 'actor')
      return {
        ...pureActivity,
        freightieAuthor_id: snapshot.actorType === 'freightie' ? snapshot.actor : null,
        userAuthor_id: snapshot.actorType === 'user' ? snapshot.actor : null,
      }
    },
  }))
  .views((self) => ({
    get avatarType(): TAvatarType {
      return self.action !== 'requested' ? FORTO_AVATAR : getAvatarType(self.actor)
    },
    get avatar(): string {
      return getAvatar(self.actor)
    },
    get messageContent(): IActivityMessageSection[] {
      return getMessageBody(self.action as TBookingRequestActions, self.reason!, self.shipmentId!)
    },
    get title(): IActivityTitle {
      return self.action !== 'requested' ? { text: COMPANY_NAME } : getTitleFromActor(self.actor)
    },
  }))

function getMessageBody(
  action: TBookingRequestActions,
  reason?: string,
  shipmentId?: string
): IActivityMessageSection[] {
  if (action === 'requested') {
    return [{ type: TEXT, content: 'notification-booking_request_requested', translate: true }]
  }

  if (action === 'rejected') {
    return [
      { type: TEXT, content: 'notification-booking_request_rejected', translate: true },
      reason && { type: TEXT, content: '\n' },
      reason && {
        type: TEXT,
        content: 'activity-feed_booking_request_reason',
        translate: true,
        style: { height: 30, display: 'inline-flex', alignItems: 'flex-end' },
      },
      reason && {
        type: TEXT,
        content: ':',
        style: { height: 30, display: 'inline-flex', alignItems: 'flex-end', marginRight: 5 },
      },
      reason && { type: TEXT, content: reason, style: { height: 30, display: 'inline-flex', alignItems: 'flex-end' } },
    ].filter(Boolean) as IActivityMessageSection[]
  }

  if (action === 'approved') {
    return [
      { type: TEXT, content: 'notification-booking_request_approved', translate: true },
      { type: TEXT, content: '. ', translate: true },
      {
        type: LABELED,
        linkText: shipmentId,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        url: `/shipment/${shipmentId}`,
      },
      reason && { type: TEXT, content: '\n' },
      reason && {
        type: TEXT,
        content: 'activity-feed_booking_request_reason',
        translate: true,
        style: { height: 30, display: 'inline-flex', alignItems: 'flex-end' },
      },
      reason && {
        type: TEXT,
        content: ':',
        getStyle: (theme) => ({
          height: 30,
          display: 'inline-flex',
          alignItems: 'flex-end',
          marginRight: theme.spacing(0.6),
        }),
      },
      reason && {
        type: TEXT,
        content: reason,
        style: { height: 30, display: 'inline-flex', alignItems: 'flex-end' },
      },
    ].filter(Boolean) as IActivityMessageSection[]
  }

  if (action === 'expired') {
    return [{ type: TEXT, content: 'notification-booking_request_expired', translate: true }]
  }

  return [{ type: TEXT, content: 'notification-booking_request_default', translate: true }]
}
